import { t } from '@web/i18n';
import { AppIcons } from '@web/ui/core/atoms';
import { twcn } from '@web/ui/core/utils';

import type { TimelineState, TimelineStep } from '../voucher_timeline';

const DOT_CLASS: Record<TimelineState, string> = {
  done: 'border-tone-green-fg bg-tone-green-fg text-primary-foreground',
  cur: 'border-ai bg-ai text-primary-foreground',
  cancelled: 'border-tone-red-fg bg-tone-red-fg text-primary-foreground',
  todo: 'border-border bg-surface text-faint-foreground',
};

/**
 * The voucher status timeline: a vertical rail of dot + connector steps. Renders
 * the derived {@link TimelineStep}s — done (green ✓), cur (aubergine), cancelled
 * (red ×), todo (empty). The connector line inherits the step's accent so a
 * completed chain reads green up to the current point.
 */
export function VoucherTimeline({
  steps,
}: Readonly<{ steps: readonly TimelineStep[] }>): React.JSX.Element {
  return (
    <div className="flex flex-col">
      {steps.map((step, i) => {
        const last = i === steps.length - 1;
        return (
          <div key={step.labelKey} className="flex gap-3">
            <div className="flex w-4.5 flex-col items-center">
              <span
                className={twcn(
                  'flex h-4.5 w-4.5 flex-shrink-0 items-center justify-center rounded-pill border text-2xs',
                  DOT_CLASS[step.state],
                )}
              >
                {step.state === 'done' ? <AppIcons name="check" size={10} /> : null}
                {step.state === 'cancelled' ? <AppIcons name="close" size={10} /> : null}
              </span>
              {last ? null : (
                <span
                  className={twcn(
                    'min-h-3.5 w-px flex-1',
                    step.state === 'done' ? 'bg-tone-green-fg' : 'bg-border-soft',
                  )}
                />
              )}
            </div>
            <div className={twcn('min-w-0', last ? 'pb-0' : 'pb-4')}>
              <div
                className={twcn(
                  'text-base font-medium',
                  step.state === 'todo' ? 'text-faint-foreground' : 'text-foreground',
                )}
              >
                {t(step.labelKey)}
              </div>
              {step.at ? (
                <div className="mt-px font-mono text-card-id text-faint-foreground">{step.at}</div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
